interface ICacheEntry<T> {
  version: number;
  cachedAt: number;
  ttlMs: number;
  data: T;
}

const STORE_INFO_CACHE_VERSION = 1;
const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

const memoryCache = new Map<string, ICacheEntry<unknown>>();

const getCacheKey = (host: string): string => `storeInfoCache:v${STORE_INFO_CACHE_VERSION}:${host}`;

const getHost = (): string | null => {
  if (typeof window === "undefined") return null;
  return window.location.host || null;
};

const isValidEntry = <T>(entry: ICacheEntry<T>): boolean => {
  if (!entry || typeof entry !== "object") return false;
  if (entry.version !== STORE_INFO_CACHE_VERSION) return false;
  if (typeof entry.cachedAt !== "number") return false;
  if (typeof entry.ttlMs !== "number") return false;
  return Date.now() - entry.cachedAt <= entry.ttlMs;
};

export const getCachedStoreInfo = <T>(options?: { ttlMs?: number }): T | null => {
  const host = getHost();
  if (!host) return null;

  const key = getCacheKey(host);
  const cachedInMemory = memoryCache.get(key) as ICacheEntry<T> | undefined;
  if (cachedInMemory && isValidEntry(cachedInMemory)) {
    return cachedInMemory.data;
  }

  if (typeof localStorage === "undefined") return null;

  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as ICacheEntry<T>;
    if (!isValidEntry(parsed)) return null;

    // Allow overriding TTL without rewriting cache (treat as expired if shorter TTL is requested)
    const requestedTtlMs = options?.ttlMs;
    if (typeof requestedTtlMs === "number" && requestedTtlMs > 0) {
      if (Date.now() - parsed.cachedAt > requestedTtlMs) return null;
    }

    memoryCache.set(key, parsed as ICacheEntry<unknown>);
    return parsed.data;
  } catch {
    return null;
  }
};

export const setCachedStoreInfo = <T>(data: T, options?: { ttlMs?: number }): void => {
  const host = getHost();
  if (!host) return;

  const key = getCacheKey(host);
  const ttlMs = options?.ttlMs ?? DEFAULT_TTL_MS;
  const entry: ICacheEntry<T> = {
    version: STORE_INFO_CACHE_VERSION,
    cachedAt: Date.now(),
    ttlMs,
    data,
  };

  memoryCache.set(key, entry as ICacheEntry<unknown>);

  if (typeof localStorage === "undefined") return;

  try {
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // Best-effort cache: ignore quota/security errors
  }
};

export const clearCachedStoreInfo = (): void => {
  const host = getHost();
  if (!host) return;

  const key = getCacheKey(host);
  memoryCache.delete(key);

  if (typeof localStorage === "undefined") return;

  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

